import React, { useState, useEffect } from 'react';
import { BrowserRouter } from 'react-router-dom';
import './App.css';
import MetaSupplyApp from './MetaSupply';
import { API_BASE } from './config';
import { ThemeProvider } from './metasupply/components/ThemeContext';
import { GlobalFilterProvider } from './metasupply/components/GlobalFilterContext';
import { T } from './metasupply/components/tokens';
import { useResponsive } from './metasupply/components/useResponsive';

// ============================================================
// TYPES
// ============================================================
interface User {
  id: number;
  email: string;
  is_admin: boolean;
}

interface LoginResponse {
  success: boolean;
  user: User;
  expires_in: number;
}

const REMEMBER_ME_KEY = 'metasupply.remember_me';
const REMEMBERED_EMAIL_KEY = 'metasupply.remembered_email';

// SSO handoff resume: after login, return the browser to the issuer's
// /sso/authorize URL so the family handoff completes (deep links survive).
const getSsoResumeUrl = (): string | null => {
  const resume = new URLSearchParams(window.location.search).get('sso_resume');
  if (resume && resume.startsWith(`${API_BASE}/sso/authorize?`)) {
    return resume;
  }
  return null;
};


// ============================================================
// iREMEDY CROSS ICON (for login screen)
// ============================================================
const IRemedyCross = ({ size = 40 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 40 40" fill="none">
    <rect x="12" y="0" width="16" height="16" rx="2" fill="#163A64" />
    <rect x="0" y="12" width="16" height="16" rx="2" fill="#163A64" />
    <rect x="12" y="12" width="16" height="16" rx="2" fill="#163A64" />
    <rect x="24" y="12" width="16" height="16" rx="2" fill="#c8160a" />
    <rect x="12" y="24" width="16" height="16" rx="2" fill="#c8160a" />
  </svg>
);

// ============================================================
// MAIN APP
// ============================================================
function App() {
  const [user, setUser] = useState<User | null>(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [authLoading, setAuthLoading] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const { isMobile } = useResponsive();

  // Check existing auth on mount
  useEffect(() => {
    let cancelled = false;

    const checkAuth = async () => {
      try {
        const res = await fetch(`${API_BASE}/auth/me`, {
          credentials: 'include',
        });
        if (!cancelled && res.ok) {
          const data: User = await res.json();
          const resume = getSsoResumeUrl();
          if (resume) {
            window.location.replace(resume);
            return;
          }
          setUser(data);
        }
      } catch (error) {
        // Ignore auth check errors; user will log in manually
      } finally {
        if (!cancelled) {
          setAuthChecked(true);
        }
      }
    };

    checkAuth();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const remembered = localStorage.getItem(REMEMBER_ME_KEY) === 'true';
    const rememberedEmail = localStorage.getItem(REMEMBERED_EMAIL_KEY) || '';
    setRememberMe(remembered);
    if (rememberedEmail) {
      setEmail(rememberedEmail);
    }
  }, []);

  // Login handler
  const handleLogin = async (event: React.FormEvent) => {
    event.preventDefault();
    if (authLoading) return;

    setAuthLoading(true);
    setAuthError(null);

    try {
      const res = await fetch(`${API_BASE}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ email, password, remember_me: rememberMe }),
      });

      if (!res.ok) {
        let detail = 'Invalid credentials';
        try {
          const err = await res.json();
          if (err?.detail) detail = err.detail;
        } catch {
          // ignore parse errors
        }
        setAuthError(detail);
        return;
      }

      const data: LoginResponse = await res.json();
      const resume = getSsoResumeUrl();
      if (resume) {
        window.location.replace(resume);
        return;
      }
      setUser(data.user);
      setPassword('');
      if (rememberMe) {
        localStorage.setItem(REMEMBER_ME_KEY, 'true');
        localStorage.setItem(REMEMBERED_EMAIL_KEY, email);
      } else {
        localStorage.removeItem(REMEMBER_ME_KEY);
        localStorage.removeItem(REMEMBERED_EMAIL_KEY);
      }
    } catch (error: any) {
      setAuthError(error?.message || 'Login failed');
    } finally {
      setAuthLoading(false);
    }
  };

  // Logout handler — redirect to content site after sign-out
  const handleLogout = async () => {
    try {
      await fetch(`${API_BASE}/auth/logout`, {
        method: 'POST',
        credentials: 'include',
      });
    } catch {
      // ignore logout errors
    } finally {
      setUser(null);
      window.location.href = 'https://iremedy.com/tradespy';
    }
  };

  // Loading state
  if (!authChecked) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: T.offWhite,
        fontFamily: "'Inter', -apple-system, sans-serif",
      }}>
        <div style={{ textAlign: 'center' }}>
          <a href="https://thetradespy.ai" style={{ display: 'inline-block' }}>
            <img src="/tradespy-logo-text.png" alt="TheTradeSpy.ai" style={{ height: 56 }} />
          </a>
          <div style={{ marginTop: 16, color: T.textMid, fontSize: 14 }}>
            Loading...
          </div>
        </div>
      </div>
    );
  }

  // Login screen
  if (!user) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: T.offWhite,
        fontFamily: "'Inter', -apple-system, sans-serif",
      }}>
        <style>{`
          @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&family=IBM+Plex+Mono:wght@400;500;600;700&family=Playfair+Display:wght@700;800;900&display=swap');
          * { box-sizing: border-box; margin: 0; padding: 0; }
        `}</style>

        <div style={{
          width: '100%',
          maxWidth: 420,
          padding: isMobile ? 24 : 40,
          background: T.offWhite,
          borderRadius: 16,
          border: `1px solid ${T.border}`,
          boxShadow: T.shadowLg,
        }}>
          {/* Logo */}
          <div style={{ textAlign: 'center', marginBottom: 32 }}>
            <a href="https://thetradespy.ai" style={{ display: 'inline-block' }}>
              <img
                src="/tradespy-logo-text.png"
                alt="TheTradeSpy.ai"
                style={{
                  height: 56,
                  margin: '0 auto',
                  display: 'block',
                }}
              />
            </a>
          </div>

          {/* Login Form */}
          <form onSubmit={handleLogin}>
            <div style={{ marginBottom: 20 }}>
              <label style={{
                display: 'block',
                fontSize: 10,
                fontWeight: 700,
                color: T.textMid,
                textTransform: 'uppercase',
                letterSpacing: '0.1em',
                marginBottom: 8,
                fontFamily: "'IBM Plex Mono', 'Courier New', monospace",
              }}>
                Email
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                style={{
                  width: '100%',
                  padding: '14px 18px',
                  borderRadius: T.radiusSm,
                  border: `1px solid ${T.border}`,
                  background: T.offWhite,
                  color: T.textDark,
                  fontSize: 14,
                  fontFamily: "'Inter', -apple-system, sans-serif",
                  outline: 'none',
                  transition: 'border-color 0.2s',
                }}
                placeholder="you@company.com"
              />
            </div>

            <div style={{ marginBottom: 24 }}>
              <label style={{
                display: 'block',
                fontSize: 10,
                fontWeight: 700,
                color: T.textMid,
                textTransform: 'uppercase',
                letterSpacing: '0.1em',
                marginBottom: 8,
                fontFamily: "'IBM Plex Mono', 'Courier New', monospace",
              }}>
                Password
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                style={{
                  width: '100%',
                  padding: '14px 18px',
                  borderRadius: T.radiusSm,
                  border: `1px solid ${T.border}`,
                  background: T.offWhite,
                  color: T.textDark,
                  fontSize: 14,
                  fontFamily: "'Inter', -apple-system, sans-serif",
                  outline: 'none',
                  transition: 'border-color 0.2s',
                }}
                placeholder="Enter your password"
              />
            </div>

            <div style={{ marginBottom: 20 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: T.textMid, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setRememberMe(checked);
                    if (!checked) {
                      localStorage.removeItem(REMEMBER_ME_KEY);
                      localStorage.removeItem(REMEMBERED_EMAIL_KEY);
                    }
                  }}
                />
                Remember me
              </label>
            </div>

            {authError && (
              <div style={{
                marginBottom: 20,
                padding: '12px 16px',
                background: '#FEF2F2',
                border: '1px solid #FECACA',
                borderRadius: T.radiusSm,
                color: T.danger,
                fontSize: 13,
              }}>
                {authError}
              </div>
            )}

            <button
              type="submit"
              disabled={authLoading}
              style={{
                width: '100%',
                padding: '14px 24px',
                borderRadius: T.radius,
                border: 'none',
                background: T.navy,
                color: T.offWhite,
                fontSize: 14,
                fontWeight: 700,
                fontFamily: "'IBM Plex Mono', 'Courier New', monospace",
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
                cursor: authLoading ? 'not-allowed' : 'pointer',
                opacity: authLoading ? 0.7 : 1,
                transition: 'opacity 0.2s',
              }}
            >
              {authLoading ? 'Signing in...' : 'Sign In'}
            </button>
          </form>


        </div>
      </div>
    );
  }

  // Main iRemedy.ai App
  return (
    <ThemeProvider>
      <BrowserRouter>
        <GlobalFilterProvider>
          <MetaSupplyApp user={user} onLogout={handleLogout} />
        </GlobalFilterProvider>
      </BrowserRouter>
    </ThemeProvider>
  );
}

export default App;
